import React, { useMemo } from 'react';
import { Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ShipInvPayRow, CurrencySummary, QueryResponse } from '../../../services/g319Api';

interface ResultTableProps {
  result: QueryResponse;
}

const fmtAmt = (v: number | null | undefined): string => {
  const val = v ?? 0;
  if (val === 0) return '0.00';
  return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const statusTag = (status: string): React.ReactNode => {
  if (!status) return null;
  const color = status === 'P' || status === 'Z' ? 'green' : 'orange';
  const label = status === 'P' ? '已核准' : status === 'Z' ? '已核准' : status;
  return <Tag color={color}>{label}</Tag>;
};

const detailColumns: ColumnsType<ShipInvPayRow & { _key: string }> = [
  { title: '交運單號', dataIndex: 'shipNo', key: 'shipNo', width: 90, fixed: 'left' },
  { title: '序', dataIndex: 'seqNo', key: 'seqNo', width: 40, align: 'center' },
  { title: '交運日期', dataIndex: 'shipDate', key: 'shipDate', width: 100 },
  {
    title: '客戶代號',
    dataIndex: 'custCode',
    key: 'custCode',
    width: 100,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.custName || '').trim()}`.trim(),
  },
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 50, align: 'center' },
  {
    title: '交運金額',
    dataIndex: 'shipAmount',
    key: 'shipAmount',
    width: 110,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '類別',
    dataIndex: 'sourceType',
    key: 'sourceType',
    width: 60,
    render: (v: string) => {
      const color = v === '交運' ? 'blue' : v === '樣品' ? 'purple' : v === '庫存' ? 'cyan' : undefined;
      return v ? <Tag color={color}>{v}</Tag> : null;
    },
  },
  {
    title: '交運狀態',
    dataIndex: 'shipStatus',
    key: 'shipStatus',
    width: 70,
    render: (v: string) => statusTag(v),
  },
  { title: '繳款單號', dataIndex: 'payNo', key: 'payNo', width: 80 },
  {
    title: '繳款金額',
    dataIndex: 'payAmount',
    key: 'payAmount',
    width: 110,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
  { title: '繳款日期', dataIndex: 'payDate', key: 'payDate', width: 100 },
  {
    title: '繳款狀態',
    dataIndex: 'payStatus',
    key: 'payStatus',
    width: 70,
    render: (v: string) => statusTag(v),
  },
  { title: '發票號碼', dataIndex: 'invNo', key: 'invNo', width: 100 },
  {
    title: '發票金額',
    dataIndex: 'invAmount',
    key: 'invAmount',
    width: 110,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
  { title: '發票日期', dataIndex: 'invDate', key: 'invDate', width: 100 },
  { title: '發票種類', dataIndex: 'invTypeName', key: 'invTypeName', width: 120, ellipsis: true },
  {
    title: '發票狀態',
    dataIndex: 'invStatus',
    key: 'invStatus',
    width: 70,
    render: (v: string) => statusTag(v),
  },
  {
    title: '未開發票金額',
    dataIndex: 'unbilledAmt',
    key: 'unbilledAmt',
    width: 120,
    align: 'right',
    render: (v: number) => {
      const color = v > 0 ? 'red' : v < 0 ? 'green' : undefined;
      return <span style={{ color }}>{fmtAmt(v)}</span>;
    },
  },
  {
    title: '發票/繳款差數',
    dataIndex: 'invPayDiffAmt',
    key: 'invPayDiffAmt',
    width: 120,
    align: 'right',
    render: (v: number) => {
      const color = v > 0 ? 'red' : v < 0 ? 'green' : undefined;
      return <span style={{ color }}>{fmtAmt(v)}</span>;
    },
  },
];

const summaryColumns: ColumnsType<CurrencySummary & { _key: string }> = [
  { title: '幣別', dataIndex: 'currency', key: 'currency', width: 60 },
  {
    title: '交運金額合計',
    dataIndex: 'shipAmount',
    key: 'shipAmount',
    width: 130,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '發票金額合計',
    dataIndex: 'invAmount',
    key: 'invAmount',
    width: 130,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '未開發票金額合計',
    dataIndex: 'unbilledAmt',
    key: 'unbilledAmt',
    width: 140,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '繳款金額合計',
    dataIndex: 'payAmount',
    key: 'payAmount',
    width: 130,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
  {
    title: '發票/繳款差數合計',
    dataIndex: 'invPayDiffAmt',
    key: 'invPayDiffAmt',
    width: 150,
    align: 'right',
    render: (v: number) => fmtAmt(v),
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const detailData = useMemo(
    () =>
      (result.details || []).map((row, i) => ({
        ...row,
        _key: `${row.shipNo}-${row.seqNo}-${row.payNo}-${row.invNo}-${i}`,
      })),
    [result.details]
  );

  const summaryData = useMemo(
    () =>
      (result.currencySummaries || []).map((row, i) => ({
        ...row,
        _key: `${row.currency}-${i}`,
      })),
    [result.currencySummaries]
  );

  const items = [
    {
      key: 'details',
      label: `交運開票繳款明細 (${result.totalCount})`,
      children: (
        <Table
          columns={detailColumns}
          dataSource={detailData}
          rowKey="_key"
          size="small"
          scroll={{ x: 2000, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
        />
      ),
    },
    {
      key: 'summary',
      label: `幣別彙總 (${summaryData.length})`,
      children: (
        <Table
          columns={summaryColumns}
          dataSource={summaryData}
          rowKey="_key"
          size="small"
          scroll={{ x: 700, y: 300 }}
          pagination={false}
        />
      ),
    },
  ];

  return <Tabs items={items} />;
};

export default ResultTable;
